/**
 * Wave-5 / A10 — Point Cloud Mode Switch controller.
 *
 * Pure consumer-side helpers that decide whether a single gTwin scene
 * supports both a "Combined Scene" (Giro3D) and a "Point Cloud Only"
 * (Potree) render path, and that preserve view state when the operator
 * pivots between the two.
 *
 * Reads ONLY existing scene-package fields — same surface A8's
 * `selectEngineForScene` consumes. G1 — no scene-package shape change.
 * G7 — every function is side-effect free, fully deterministic.
 *
 * Surface contract (matches `docs/design-briefs/A10-pointcloud-mode-switch.md`):
 *   - `availableModesFor` returns the deduplicated, ordered list of
 *     modes available for the current scene. Both modes are returned
 *     only when:
 *       1. The scene is point-cloud-only (no non-point-cloud items).
 *       2. At least one point-cloud SceneItem nominates an
 *          `alternateSceneItemIds` entry that resolves to a SceneItem
 *          of a different artifact-kind family
 *          (i.e. COPC <-> Potree-v2-octree).
 *     Otherwise only the default mode is returned, which keeps the
 *     toggle hidden (G8 reversibility).
 *   - `sceneItemsFor` filters the scene's point-cloud SceneItems down
 *     to the ones that should render on the chosen mode.
 *   - `preserveState` returns the cross-engine state record the host
 *     replays after the new adapter mounts: camera, selection ids,
 *     classification filter, and the persisted point-cloud quality
 *     snapshot. Engine-specific buffers are explicitly cleared.
 */

import type { SceneSlot } from '@/sdk-stub/gtwin-sdk';
import type {
  PointCloudQualitySettings,
  PointCloudQualitySnapshot,
} from '@/engines/giro3d/Giro3DAdapter';
import type { ScenePackageDocument } from './scenePackage';
import {
  selectEngineForScene,
  type EngineSelection,
  type PotreeBackendStatusInput,
} from './engineRouting';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type SceneMode = 'combined-scene' | 'point-cloud-only';

/**
 * Per-mode preserved view state. Engine-specific GL buffers are NOT
 * preserved; the destination adapter rebuilds them on mount.
 */
export interface PreservedSceneState {
  /** Camera snapshot from `useViewerStore.camera`. Opaque to the controller. */
  readonly camera: CameraState | null;
  /** Selected scene-item / feature ids. */
  readonly selectionIds: ReadonlyArray<string>;
  /** ASPRS classification codes the operator has filtered to. */
  readonly classificationFilterIds: ReadonlyArray<number>;
  /**
   * Persisted point-cloud quality snapshot — shared by Giro3D and Potree.
   * Reuses the canonical snapshot type from `Giro3DAdapter` so callers do
   * not have to translate between adapters.
   */
  readonly pointCloudQuality: PointCloudQualitySnapshot | null;
  /** Engine-specific buffers. Always cleared by `preserveState`. */
  readonly engineBuffers: null;
}

export interface CameraState {
  readonly position?: ReadonlyArray<number>;
  readonly target?: ReadonlyArray<number>;
  readonly zoom?: number;
  readonly bearing?: number;
  readonly pitch?: number;
}

/**
 * Settings sub-set replayed through the new adapter after a mode swap.
 * Matches `PointCloudQualitySettings` so `adapter.setPointCloudQuality`
 * accepts the value without translation.
 */
export type PointCloudQualityReplayInput = Partial<PointCloudQualitySettings>;

// ---------------------------------------------------------------------------
// availableModesFor
// ---------------------------------------------------------------------------

/**
 * Returns the modes available for the supplied scene-package, deduplicated
 * and ordered with the package default first.
 *
 * Both modes are returned only when the scene is point-cloud-only AND at
 * least one point-cloud SceneItem references an alternate item of the
 * complementary artifact family (COPC <-> Potree-v2-octree). When the
 * scene mixes models with point clouds the toggle is hidden — Giro3D is
 * the only valid renderer per A8.
 */
export function availableModesFor(
  scenePackage: ScenePackageDocument | null | undefined,
): SceneMode[] {
  const items = collectItems(scenePackage);
  if (items.length === 0) return ['combined-scene'];

  if (items.some((item) => !item.isPointcloud)) {
    return ['combined-scene'];
  }

  const hasCopc = items.some((item) => item.isCopc);
  const hasPotree = items.some((item) => item.isPotreeV2);
  if (!hasCopc || !hasPotree) {
    return [defaultMode(scenePackage, items)];
  }

  const alternateBridgesFamilies = items.some((item) => {
    if (item.alternateSceneItemIds.length === 0) return false;
    for (const altId of item.alternateSceneItemIds) {
      const target = items.find((candidate) => candidate.id === altId);
      if (!target) continue;
      if (item.isCopc && target.isPotreeV2) return true;
      if (item.isPotreeV2 && target.isCopc) return true;
    }
    return false;
  });

  if (!alternateBridgesFamilies) {
    return [defaultMode(scenePackage, items)];
  }

  const first = defaultMode(scenePackage, items);
  const second: SceneMode =
    first === 'point-cloud-only' ? 'combined-scene' : 'point-cloud-only';
  return [first, second];
}

// ---------------------------------------------------------------------------
// sceneItemsFor
// ---------------------------------------------------------------------------

/**
 * Returns the SceneItem ids that should render on the supplied mode.
 *
 * - `"point-cloud-only"` -> Potree-v2-octree items, ordered with the
 *   package default first when present.
 * - `"combined-scene"` -> COPC + every non-point-cloud item, with the
 *   package default first when present (mirrors A8's mixed-scene
 *   ordering).
 *
 * Falls back to all SceneItem ids when the scene has no point cloud at
 * all (preserves G8 reversibility for non-point-cloud packages).
 */
export function sceneItemsFor(
  mode: SceneMode,
  scenePackage: ScenePackageDocument | null | undefined,
): string[] {
  const items = collectItems(scenePackage);
  if (items.length === 0) return [];
  const allIds = items.map((item) => item.id);
  const packageDefault = readPackageDefault(scenePackage);

  if (mode === 'point-cloud-only') {
    const ids = items.filter((item) => item.isPotreeV2).map((item) => item.id);
    if (ids.length === 0) return orderByDefault(allIds, packageDefault);
    return orderByDefault(ids, packageDefault);
  }

  // "combined-scene": every non-point-cloud item plus every COPC item.
  // Potree-v2 items are excluded because Giro3D cannot render them
  // (A8's mixed-scene contract).
  const combinedIds = items
    .filter((item) => !item.isPotreeV2)
    .map((item) => item.id);
  if (combinedIds.length === 0) return orderByDefault(allIds, packageDefault);
  return orderByDefault(combinedIds, packageDefault);
}

// ---------------------------------------------------------------------------
// preserveState
// ---------------------------------------------------------------------------

/**
 * Carry user view state across a mode switch. Returns a new
 * `PreservedSceneState` — never mutates the input. When `prevMode ===
 * nextMode` the record is still rebuilt (no aliasing) so callers can
 * safely treat the result as the canonical next state.
 *
 * Engine-specific buffers (Giro3D `COPCSource` caches / Potree octree
 * tiles) are intentionally `null` so the destination adapter rebuilds
 * them on mount.
 */
export function preserveState(
  prevMode: SceneMode,
  nextMode: SceneMode,
  prevState: Partial<PreservedSceneState> | null | undefined,
): PreservedSceneState {
  const camera = prevState?.camera ?? null;
  const selectionIds = Array.isArray(prevState?.selectionIds)
    ? [...prevState.selectionIds]
    : [];
  const classificationFilterIds = Array.isArray(
    prevState?.classificationFilterIds,
  )
    ? [...prevState.classificationFilterIds]
    : [];
  const pointCloudQuality = prevState?.pointCloudQuality ?? null;
  // `prevMode` / `nextMode` are accepted for symmetry with the engine
  // routing API; today they do not influence the carried state because
  // the preserved fields are engine-agnostic. Including them in the
  // signature keeps room for future per-mode reset behaviors without
  // a breaking change.
  void prevMode;
  void nextMode;
  return {
    camera,
    selectionIds,
    classificationFilterIds,
    pointCloudQuality,
    engineBuffers: null,
  };
}

// ---------------------------------------------------------------------------
// nextEngineForMode
// ---------------------------------------------------------------------------

/**
 * Re-evaluate `selectEngineForScene` for a chosen mode by synthesizing a
 * derived scene-package whose `extensions.pointcloud.defaultMode` is
 * overridden to the requested mode. This keeps A8 as the single source
 * of truth for engine routing — the mode switch is a UX-level shim that
 * asks A8 the same question with a different hint.
 *
 * Returns the `EngineSelection` produced by `selectEngineForScene`.
 */
export function nextEngineForMode(
  scenePackage: ScenePackageDocument | null | undefined,
  mode: SceneMode,
  potreeStatus: PotreeBackendStatusInput,
): EngineSelection {
  if (!scenePackage) {
    return selectEngineForScene(null, { potreeStatus });
  }
  const existingExt = isObject(
    (scenePackage as { extensions?: unknown }).extensions,
  )
    ? ((scenePackage as { extensions?: unknown }).extensions as JsonObject)
    : {};
  const existingPc = isObject(existingExt.pointcloud)
    ? (existingExt.pointcloud as JsonObject)
    : {};
  const derived: ScenePackageDocument = {
    ...scenePackage,
    extensions: {
      ...existingExt,
      pointcloud: {
        ...existingPc,
        defaultMode: mode,
      },
    },
  } as ScenePackageDocument;
  return selectEngineForScene(derived, { potreeStatus });
}

// ---------------------------------------------------------------------------
// Internal — normalize the scene-package's point-cloud entries
// ---------------------------------------------------------------------------

type JsonObject = Record<string, unknown>;

interface NormalizedItem {
  readonly id: string;
  readonly slot: SceneSlot;
  readonly layerKind: string;
  readonly artifactKind: string | null;
  readonly sourceGroupId: string | null;
  readonly alternateSceneItemIds: ReadonlyArray<string>;
  readonly isPointcloud: boolean;
  readonly isPotreeV2: boolean;
  readonly isCopc: boolean;
}

const NON_POINTCLOUD_LAYER_KINDS = new Set<string>([
  'raster',
  'vector',
  'terrain',
  'bim',
  '3dtiles',
  'panorama',
  'timeseries',
]);

function collectItems(
  scenePackage: ScenePackageDocument | null | undefined,
): NormalizedItem[] {
  if (!scenePackage || !isObject(scenePackage.slots)) return [];
  const result: NormalizedItem[] = [];
  const slots = scenePackage.slots as Record<string, unknown>;
  for (const slotName of Object.keys(slots)) {
    const slot = slotName as SceneSlot;
    const raw = slots[slotName];
    const entries = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object'
        ? [raw]
        : [];
    for (const entry of entries) {
      if (!isObject(entry)) continue;
      const normalized = normalizeItem(entry, slot);
      if (normalized) result.push(normalized);
    }
  }
  return result;
}

function normalizeItem(raw: JsonObject, slot: SceneSlot): NormalizedItem | null {
  const id = typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : null;
  if (!id) return null;
  const layerKind = typeof raw.layerKind === 'string' ? raw.layerKind : '';
  const artifactKind = typeof raw.artifactKind === 'string' ? raw.artifactKind : null;
  const extensions = isObject(raw.extensions) ? raw.extensions : undefined;
  const pointcloudExt =
    extensions && isObject(extensions.pointcloud) ? extensions.pointcloud : undefined;

  const sourceGroupId =
    pointcloudExt &&
    typeof pointcloudExt.sourceGroupId === 'string' &&
    pointcloudExt.sourceGroupId.length > 0
      ? pointcloudExt.sourceGroupId
      : null;
  const alternateSceneItemIds =
    pointcloudExt && Array.isArray(pointcloudExt.alternateSceneItemIds)
      ? pointcloudExt.alternateSceneItemIds.filter(
          (value): value is string => typeof value === 'string',
        )
      : [];

  const isPotreeV2 = artifactKind === 'potree-v2-octree';
  const isCopc = artifactKind === 'copc';
  const is3dTilesPointcloud = artifactKind === '3dtiles-pointcloud';
  const isPointcloud =
    (layerKind === 'pointcloud' || isPotreeV2 || isCopc || is3dTilesPointcloud) &&
    !NON_POINTCLOUD_LAYER_KINDS.has(layerKind);

  return {
    id,
    slot,
    layerKind,
    artifactKind,
    sourceGroupId,
    alternateSceneItemIds,
    isPointcloud,
    isPotreeV2,
    isCopc,
  };
}

function defaultMode(
  scenePackage: ScenePackageDocument | null | undefined,
  items: ReadonlyArray<NormalizedItem>,
): SceneMode {
  const ext = readPackagePointcloudExtensions(scenePackage);
  const declared = typeof ext?.defaultMode === 'string' ? ext.defaultMode : null;
  if (declared === 'point-cloud-only') return 'point-cloud-only';
  if (declared === 'combined-scene') return 'combined-scene';
  // No explicit hint — keep the conservative A8 default (Giro3D /
  // combined) unless the only available artifacts are Potree-v2.
  if (items.length > 0 && items.every((item) => item.isPotreeV2)) {
    return 'point-cloud-only';
  }
  return 'combined-scene';
}

function readPackagePointcloudExtensions(
  scenePackage: ScenePackageDocument | null | undefined,
): JsonObject | null {
  if (!scenePackage) return null;
  const ext = (scenePackage as { extensions?: unknown }).extensions;
  if (!isObject(ext)) return null;
  return isObject(ext.pointcloud) ? ext.pointcloud : null;
}

function readPackageDefault(
  scenePackage: ScenePackageDocument | null | undefined,
): string | null {
  const ext = readPackagePointcloudExtensions(scenePackage);
  if (!ext) return null;
  return typeof ext.defaultSceneItemId === 'string' ? ext.defaultSceneItemId : null;
}

function orderByDefault(
  ids: ReadonlyArray<string>,
  packageDefault: string | null,
): string[] {
  if (!packageDefault) return [...ids];
  if (!ids.includes(packageDefault)) return [...ids];
  return [packageDefault, ...ids.filter((id) => id !== packageDefault)];
}

function isObject(value: unknown): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
